import { useEffect, useState } from "react";
import { Container, Col, Row } from "reactstrap";
import { Component as Form } from "@repoflow/form-schema";
import { BreadcrumbItem } from "reactstrap";
import { useHistory } from "react-router-dom";
import * as Notify from "@repoflow/form-schema/notify";
import * as BucketFileQL from "queries/bucket-file";
import { CancellableContent } from "@repoflow/components/content-cancel";
import {
  Mutation,
  Button,
  useSectionContext,
} from "@repoflow/components/section";
import { postForm } from "@repoflow/browser/file-form-client";
import { PRIVATE } from "@common/schema/bucket-file/visibility";

export const Sidebar = () => null;

export const Indice = () => <BreadcrumbItem>New</BreadcrumbItem>;

const useUploadState = () => {
  const [{ file, phase }, setState] = useState({ file: null, phase: "init" });

  return {
    file,
    phase,
    setPhase: (phase) => setState((state) => ({ ...state, phase })),
    setFile: (file) => setState((state) => ({ ...state, file })),
  };
};

export const Content = ({
  match: {
    params,
    params: { bucketId },
  },
}) => {
  const uploadState = useUploadState();
  const { phase, setPhase } = uploadState;
  const history = useHistory();
  // The bucket view, which lists the files. replace(), not goBack(): where you
  // landed used to depend on how you reached the form.
  const {
    parent: { base },
  } = useSectionContext();

  useEffect(() => {
    if (phase === "complete") {
      history.replace(base);
    }
  }, [phase]);

  // `visibility` is seeded with the same string the select's first option shows,
  // and it is PRIVATE — an upload is not a decision to publish. Somebody who
  // wants sixteen public images picks the option once and every file in that
  // batch carries it; nothing is published by not looking at the control.
  const initial = {
    file: null,
    tags: [],
    visibility: PRIVATE,
  };
  return (
    <Mutation
      initial={initial}
      context={{ uploadState }}
      target={{ $ref: "/Public/BucketFile/Create" }}
      mutation={BucketFileQL.create}
      update={BucketFileQL.update(params)}
      onMutate={({ state: { values }, handler }) => {
        const {
          file: { hash, ext },
          tags,
          visibility
        } = values;

        setPhase("loading");

        handler({
          variables: {
            bucketId,
            input: { hash, ext, tags, visibility },
          },
        });
      }}
      onCompleted={(
        data,
        {
          state: {
            values: {
              file: { file },
            },
          },
        }
      ) => {
        const {
          viewer: {
            account: {
              buckets: {
                get: {
                  files: {
                    create: { form },
                  },
                },
              },
            },
          },
        } = data;

        postForm(form, file, {
          onSuccess: (data) => {
            setPhase("complete");
          },
          onError: (error) => {
            setPhase("complete");
            if (
              error.responseText &&
              error.responseText.indexOf("EntityTooLarge") !== -1
            ) {
              Notify.error(
                <span>
                  Your image size is too large, please let us know to increase
                  the allowed size or reduce your image size.
                </span>
              );
            } else {
              Notify.error(<span>Error during upload operation.</span>);
            }
          },
        });
      }}
      onError={(error) => {
        // The mutation failed, so no file record was created and retrying is
        // clean. Without this the Create button keeps spinning, disabled,
        // forever. (The postForm failure path below is different: the record
        // does exist by then, so it lands you on the bucket view where you can
        // see the file's actual state.)
        setPhase("init");
        Notify.error(error.toString());
      }}
    >
      {MutationHandler}
    </Mutation>
  );
};

const MutationHandler = ({
  values: bucket,
  schema,
  state,
  mutate,
  context: {
    phase
  },
  target,
}) => {
  const { values, setValues } = state;
  
  const formProps = {
    schema,
    target,
    onSubmit: () => {
      mutate();
    },
    onChange: (vals) => {
      setValues(vals);
    },
    config: {
      "/Public/BucketFile/Create": {
        fields: [
          {
            property: "file",
            allowed: bucket.exts,
            minFileSize: bucket.minFileSize,
            maxFileSize: bucket.maxFileSize,
          },
          "tags",
          "visibility"
        ],
      },
    },
    values,
  };

  return (
    <CancellableContent>
      <Container className="mt-4">
        <Row>
          <Col>
            <Form {...formProps}>
              <Button type="submit" loading={phase === "loading"}>
                Create
              </Button>
            </Form>
          </Col>
        </Row>
      </Container>
    </CancellableContent>
  );
};
